import React, { useEffect, useState, useCallback } from "react";
import ActionButtons from "../../../components/ui/popups/ActionButtons";
import OtpPopup from "../../../components/ui/popups/OtpPopup";
import { Preferences } from "@capacitor/preferences";
import { decodeInvitationToken } from "../../../services/sendrequest";
import { acceptInvitationAPI, followUser, rejectFollowRequest } from "../../../services/datashare";
import { notifyInvitationTokenChanged } from "../../../services/staffPortal";

// ============================================================================
// Types & Interfaces
// ============================================================================

interface ToastBar {
  success: (msg: string, duration?: number, position?: string) => void;
  error: (msg: string, duration?: number, position?: string) => void;
}

interface User {
  email?: string | null;
  phone?: string | null;
  profile_id?: number | null;
}

interface InvitedNotificationProps {
  toastbar: ToastBar;
  user: User | null;
  onProfileUpdate?: (field: "email" | "phone", value: string) => void;
}

type ContactMode = "phone" | "email" | null;

interface InvitationInformation {
  followerRelationData?: any;
  invitationData: {
    isValid: boolean;
    inviterProfileId: string;
    countryCode: string;
    invitation_method: string;
    userIdentifier?: string;
  };
  isExistingRelation: boolean;
  popup: {
    title: string;
    message: string;
    actionButtons: {
      text: string;
      action: () => Promise<void>;
      isPrimary: boolean;
    };
  };
  inviterProfileId?: string;
  countryCode?: string;
  userIdentifier?: string;
  invitedTo?: string;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_COUNTRY_CODE = "+1";
const INVITATION_TOKEN_KEY = "invitation_token";
const TOAST_DURATION = 3000;
const TOAST_POSITION_TOP = "top";
const TOAST_POSITION_BOTTOM = "bottom";

const INITIAL_INVITATION_INFO: InvitationInformation = {
  followerRelationData: undefined,
  invitationData: {
    isValid: false,
    inviterProfileId: "",
    countryCode: "",
    invitation_method: "",
    userIdentifier: "",
  },
  isExistingRelation: false,
  popup: {
    title: "",
    message: "",
    actionButtons: {
      text: "",
      action: async () => Promise.resolve(),
      isPrimary: true,
    },
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

const isEmailIdentifier = (identifier: string): boolean => {
  return identifier.includes("@");
};

// ============================================================================
// Main Component
// ============================================================================

export const InvitedNotification: React.FC<InvitedNotificationProps> = ({
  toastbar,
  user,
  onProfileUpdate,
}) => {
  // Popup visibility states
  const [showInvitationPopup, setShowInvitationPopup] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);

  // Invitation data
  const [invitationInformation, setInvitationInformation] =
    useState<InvitationInformation>(INITIAL_INVITATION_INFO);

  // Contact form states
  const [mode, setMode] = useState<ContactMode>(null);
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [newPhone, setNewPhone] = useState(user?.phone || "");
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    checkInvitation();
  }, [user?.profile_id]);
  useEffect(() => {
    setPhone(user?.phone || "");
    setEmail(user?.email || "");
    setNewPhone(user?.phone || "");
    setNewEmail(user?.email || "");
  }, [user?.phone, user?.email]);
  const [showTitle, setShowTitle] = useState("");
  const [showMessage, setShowMessage] = useState("");
  const [actionButtons, setActionButtons] = useState<any>(null);
  // ============================================================================
  // Invitation Checking Logic
  // ============================================================================
  const checkInvitation = useCallback(async () => {
    if (!user?.profile_id) return;

    try {
      const { value: storedToken } = await Preferences.get({
        key: INVITATION_TOKEN_KEY,
      });

      if (!storedToken) {
        return;
      }

      const invitationDetails: any = await decodeInvitationToken(storedToken);
      console.log("Decoded invitation data:", invitationDetails);
      const { invitationData, isExistingRelation, popup} = invitationDetails.data;

      console.log(invitationDetails?.data.popup);
      console.log(invitationDetails.data.invitationData);
      setShowTitle(invitationDetails?.data.popup?.title);
      setShowMessage(invitationDetails?.data.popup?.message);
      setActionButtons(invitationDetails?.data.popup?.actionButtons?.[0]);
      setInvitationInformation({
        followerRelationData: invitationData?.followerRelationsData,
        invitationData: invitationData,
        isExistingRelation: isExistingRelation,
        popup: popup,
        inviterProfileId: invitationData.inviterProfileId,
        countryCode: invitationData.phone_country_code,
        userIdentifier: invitationData.userIdentifier,
        invitedTo: invitationData.invitedTo,
      });
      setShowInvitationPopup(true);
    } catch (error) {
      console.error("Error checking invitation:", error);
    }
  }, [user]);

  // ============================================================================
  // Action Handlers
  // ============================================================================

  const handleAcceptInvitation = async () => {
    try {
      const response = await acceptInvitationAPI({
        inviter_profile_id:
          invitationInformation.invitationData.inviterProfileId,
        invited_profile_id: user?.profile_id,
        invitation_method:
          invitationInformation.invitationData.invitation_method,
      });
      toastbar.success(
        "Invitation accepted!",
        TOAST_DURATION,
        TOAST_POSITION_BOTTOM
      );
    } catch (error) {
      const errorMessage =
        (error as Error)?.message || "Failed to accept invitation";
      toastbar.error(errorMessage, TOAST_DURATION, TOAST_POSITION_BOTTOM);
      console.error("Error accepting invitation:", error);
    }
    await cleanupInvitation();
    setShowInvitationPopup(false);
  };

  const handleSendInvitation = async () => {
    try {
      const payload = buildFollowUserPayload();
      await followUser(payload);

      await cleanupInvitation();
      toastbar.success(
        "Invitation sent!",
        TOAST_DURATION,
        TOAST_POSITION_BOTTOM
      );
    } catch (error) {
      console.error("Error sending invitation:", error);
      toastbar.error(
        "Failed to send invitation",
        TOAST_DURATION,
        TOAST_POSITION_BOTTOM
      );
    }
  };

  const buildFollowUserPayload = () => {
    const inviterProfileIdNum =
      Number(invitationInformation.inviterProfileId) || 0;
    const currentProfileIdNum =
      user?.profile_id != null ? Number(user.profile_id) : 0;

    const isShareInvitation = invitationInformation.invitedTo === "share";
    return {
      user_identifier: invitationInformation.userIdentifier,
      invitation_method: isShareInvitation ? "follow" : "share",
      follower_profile_id: isShareInvitation
        ? inviterProfileIdNum
        : currentProfileIdNum,
      following_profile_id: isShareInvitation
        ? currentProfileIdNum
        : inviterProfileIdNum,
    };
  };

  const handleDeclineInvitation = async () => {
    try {
      if (!invitationInformation.followerRelationData) return;
      if (invitationInformation.followerRelationData.relationship_type === "share") {
        await rejectFollowRequest({ relationship_id: invitationInformation.followerRelationData.id });
      } else if (invitationInformation.followerRelationData.relationship_type === "follow") {
        await rejectFollowRequest({ relationship_id: invitationInformation.followerRelationData.id });
      }
      toastbar.success(
        "Invitation declined",
        TOAST_DURATION,
        TOAST_POSITION_BOTTOM
      );
    } catch (error) {
      const errorMessage =
        (error as Error)?.message || "Failed to decline invitation";
      toastbar.error(errorMessage, TOAST_DURATION, TOAST_POSITION_BOTTOM);
      console.error("Error declining invitation:", error);
    }
    await cleanupInvitation();
    setShowInvitationPopup(false);

  };

  // const handleCloseSelfInviteWarning = async () => {
  //   await cleanupInvitation();
  // };

  const cleanupInvitation = async (showPopup = true) => {
    await Preferences.remove({ key: INVITATION_TOKEN_KEY });
    // R-753: the invite is settled, so the staff portal pop-up may now show.
    notifyInvitationTokenChanged();
    if (showPopup) {
      setShowInvitationPopup(false);
    }
  };
  const handleAddToProfile = () => {
    const identifier: string = invitationInformation.invitationData.userIdentifier ?? "";
    const isEmail = isEmailIdentifier(identifier);

    setMode(isEmail ? "email" : "phone");

    if (isEmail) {
      setNewEmail(identifier);
    } else {
      const invitationCountryCode =
        invitationInformation.countryCode || DEFAULT_COUNTRY_CODE;
      setCountryCode(invitationCountryCode);
      setNewPhone(identifier.replace(invitationCountryCode, ""));
    }

    setShowInvitationPopup(false);
    setShowOtpModal(true);
    cleanupInvitation(false);
  };
  const handleCloseOtpModal = () => {
    setNewPhone(phone);
    setNewEmail(email);
    setShowOtpModal(false);
    setMode(null);
  };

  const handleSaveProfile = async (
    contact: string,
    type: "phone" | "email"
  ) => {
    if (type === "phone") {
      setPhone(contact);
      onProfileUpdate?.("phone", contact);
    } else if (type === "email") {
      setEmail(contact);
      onProfileUpdate?.("email", contact);
    }

    handleCloseOtpModal();
    await handleAcceptInvitation();
  };

  return (
    <>
      {/* Self-Invite Warning Popup */}
      {showInvitationPopup && (
        <ActionButtons
          isOpen={showInvitationPopup}
        onClose={() => {
          setShowInvitationPopup(false);
        }}
        title={showTitle}
        message={showMessage}
        actionButtons={
          actionButtons
            ? (() => {
              switch (actionButtons?.action) {
                case "close":
                  return [
                    {
                      text: actionButtons?.text || "Close",
                      action: async () => {
                        setShowInvitationPopup(false);
                        await cleanupInvitation();
                      },
                      isPrimary: actionButtons?.isPrimary ?? true,
                    },
                  ];
                case "accept":
                  return [
                    {
                      text:
                        actionButtons?.text ||
                        (invitationInformation.invitedTo === "share"
                          ? "Accept Invitation"
                          : "Approve Request"),
                      action: handleAcceptInvitation,
                      isPrimary: actionButtons?.isPrimary ?? true,
                    },
                    {
                      text: "Decline",
                      action: async () => {
                        await handleDeclineInvitation();
                      },
                      isPrimary: false,
                    },
                  ];
                case "add_to_profile":
                  return [
                    {
                      text: "Yes, Add",
                      action: async () => {
                        // Add to profile logic here
                        await handleAddToProfile();
                      },
                      isPrimary: true,
                    },
                    {
                      text: "No",
                      action: async () => {
                        setShowInvitationPopup(false);
                        await cleanupInvitation();
                      },
                      isPrimary: false,
                    },
                  ];
                default:
                  return [
                    {
                      text: "Close",
                      action: async () => {
                        setShowInvitationPopup(false);
                        await cleanupInvitation();
                      },
                      isPrimary: true,
                    },
                  ];
              }
            })()
            : [
              {
                text: "Close",
                action: async () => {
                  setShowInvitationPopup(false);
                  await cleanupInvitation();
                },
                isPrimary: true,
              },
            ]
        }
      />
      )}
      {mode === "phone" && showOtpModal && (
        <OtpPopup
          isOpen={showOtpModal}
          onClose={handleCloseOtpModal}
          phone={newPhone}
          setPhone={setNewPhone}
          email={email}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          setEmail={setNewEmail}
          userPhone={user?.phone ?? ""}
          userEmail={user?.email ?? ""}
          handleSaveProfile={handleSaveProfile}
          showToastMessage={() =>
            console.log("Phone has been updated successfully")
          }
          mode="phone"
          skipDuplicateCheck={true}
        />
      )}

      {/* Email OTP Modal */}
      {mode === "email" && showOtpModal && (
        <OtpPopup
          isOpen={showOtpModal}
          onClose={handleCloseOtpModal}
          phone={newPhone}
          setPhone={setNewPhone}
          email={newEmail}
          setEmail={setNewEmail}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          userPhone={user?.phone ?? ""}
          userEmail={user?.email ?? ""}
          handleSaveProfile={handleSaveProfile}
          showToastMessage={() =>
            console.log("Email has been updated successfully")
          }
          mode="email"
          skipDuplicateCheck={true}
        />
      )}
    </>
  );
};
